import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ListChecks } from "lucide-react";
import PilotBanner from "@/modules/racket_lab_admin/PilotBanner";
import RLNavSidebar from "@/modules/racket_lab_admin/RLNavSidebar";
import useAdminApi from "@/hooks/useAdminApi";
import useRLContext from "@/hooks/useRLContext";
import { useRacketLabAdminT } from "@/i18n/racketLabAdminI18n";
import { TICKET_STATUS_LABEL_KEY, labelKeyFor } from "@/i18n/racketLabMaps";

/**
 * RLAdminMyTickets — SS-3 (Feb 2026).
 *
 * Admin-only "My tickets" view that filters the workshop bandeja by a
 * chosen operator. Re-uses `GET /api/admin/racket-lab/tickets?
 * racket_lab_operator_id={id}` (RL-A.6 listing, filter already exists)
 * and groups the result by RL-A.6 canonical status so the operator
 * (or QA Manager impersonating one) sees a per-status work queue.
 *
 * No new backend endpoint. No customer-facing surface. Stepper
 * actions are still served by SS-1 via the detail page.
 */
const QUEUE_GROUPS = [
  "assigned",
  "in_progress",
  "pending_customer_approval",
  "ready_for_pickup",
];
const TERMINAL_GROUPS = ["closed", "cancelled", "deleted"];

const GROUP_STYLES = {
  assigned: "bg-indigo-50 border-indigo-200 text-indigo-900",
  in_progress: "bg-amber-50 border-amber-200 text-amber-900",
  pending_customer_approval: "bg-yellow-50 border-yellow-200 text-yellow-900",
  ready_for_pickup: "bg-emerald-50 border-emerald-200 text-emerald-900",
};

const RLAdminMyTickets = () => {
  const t = useRacketLabAdminT();
  const navigate = useNavigate();
  const { data: opsData } = useAdminApi("/admin/racket-lab/operators");
  const operators = useMemo(() => {
    const raw = opsData?.items || opsData?.operators || (Array.isArray(opsData) ? opsData : []);
    return raw;
  }, [opsData]);
  const [operatorId, setOperatorId] = useState("");

  // SS-8/CP-1.3 — operator profiles are locked to their OWN queue: the
  // selector is pre-filled with their linked operator entity + disabled.
  const { ctx: rlCtx } = useRLContext();
  const lockedOperatorId = rlCtx?.operator_ids?.[0] || "";
  useEffect(() => {
    if (lockedOperatorId) setOperatorId(lockedOperatorId);
  }, [lockedOperatorId]);
  const [includeTerminal, setIncludeTerminal] = useState(false);

  const { data, loading, error } = useAdminApi(
    operatorId
      ? `/admin/racket-lab/tickets?racket_lab_operator_id=${encodeURIComponent(operatorId)}&limit=200`
      : null,
  );

  // micro (Jun 2026) — low-stock warning. Read-only cross-reference of the
  // ticket's linked inventory item (SS-6) against the shop inventory list.
  // NO writes, NO stock mutations: re-uses GET /inventory (already operator
  // readable since the read-only micro) and renders an internal UI badge.
  const { data: invData } = useAdminApi(
    operatorId ? "/admin/racket-lab/inventory?limit=200" : null,
  );
  const invById = useMemo(() => {
    const map = {};
    for (const it of invData?.items || []) map[it.id] = it;
    return map;
  }, [invData]);

  const stockWarningFor = (row) => {
    if (TERMINAL_GROUPS.includes(row.status)) return null; // consumption already settled / irrelevant
    const item = row.racket_lab_inventory_item_id ? invById[row.racket_lab_inventory_item_id] : null;
    if (!item) return null;
    const available = Number(item.available_quantity || 0);
    const needed = Number(item.consumption_per_ticket || 0);
    if (needed > 0 && available < needed) return { type: "insufficient", item };
    if (item.low_stock) return { type: "low", item };
    return null;
  };

  const tickets = useMemo(() => {
    const rows = data?.items || (Array.isArray(data) ? data : []);
    return rows;
  }, [data]);

  const grouped = useMemo(() => {
    const map = {};
    for (const g of QUEUE_GROUPS) map[g] = [];
    for (const g of TERMINAL_GROUPS) map[g] = [];
    map.draft = []; // own bucket; surfaced only if any tickets are still draft for this op
    map.checked_in = [];
    for (const t of tickets) {
      const s = t.status || "draft";
      if (!map[s]) map[s] = [];
      map[s].push(t);
    }
    return map;
  }, [tickets]);

  const visibleGroups = useMemo(() => {
    const base = ["draft", "checked_in", ...QUEUE_GROUPS];
    return includeTerminal ? [...base, ...TERMINAL_GROUPS] : base;
  }, [includeTerminal]);

  const totalActive = useMemo(
    () => visibleGroups.reduce((acc, g) => acc + (grouped[g]?.length || 0), 0),
    [grouped, visibleGroups],
  );

  return (
    <div data-testid="rl-admin-my-tickets-page" className="flex flex-col gap-4">
      <PilotBanner />
      <RLNavSidebar />

      <header className="flex items-baseline justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <ListChecks className="h-5 w-5 text-emerald-700" />
            {t("my_tickets.title")}
          </h1>
          <p className="text-xs text-slate-500">{t("my_tickets.subtitle")}</p>
        </div>
        <p data-testid="rl-my-tickets-counter" className="text-xs text-slate-500">
          {operatorId ? t("my_tickets.counter", { count: totalActive }) : t("my_tickets.no_operator_selected")}
        </p>
      </header>

      <section className="flex flex-wrap items-end gap-3 rounded-md border border-slate-200 bg-white p-3">
        <label htmlFor="rl-my-tickets-operator" className="flex flex-col gap-1">
          <span className="text-[10px] uppercase tracking-wide text-slate-500">
            {t("my_tickets.operator_label")}
          </span>
          <select
            id="rl-my-tickets-operator"
            data-testid="rl-my-tickets-operator-select"
            value={operatorId}
            onChange={(e) => setOperatorId(e.target.value)}
            disabled={!!lockedOperatorId}
            className="rounded-md border border-slate-300 px-2 py-1.5 text-sm min-w-[260px] disabled:bg-slate-100 disabled:text-slate-500"
          >
            <option value="">{t("my_tickets.operator_placeholder")}</option>
            {operators.map((o) => (
              <option key={o.id} value={o.id}>
                {(o.display_name || o.id)}
                {o.status && o.status !== "active" ? ` · ${o.status}` : ""}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-xs text-slate-700">
          <input
            type="checkbox"
            data-testid="rl-my-tickets-include-terminal"
            checked={includeTerminal}
            onChange={(e) => setIncludeTerminal(e.target.checked)}
          />
          {t("my_tickets.include_terminal_label")}
        </label>
      </section>

      {!operatorId && (
        <div data-testid="rl-my-tickets-empty-state" className="rounded-md border border-dashed border-slate-300 bg-white p-6 text-center text-xs text-slate-500">
          {t("my_tickets.empty_no_operator")}
        </div>
      )}

      {operatorId && loading && (
        <div data-testid="rl-my-tickets-loading" className="rounded-md border border-slate-200 bg-white p-4 text-xs text-slate-500">
          {t("my_tickets.loading")}
        </div>
      )}

      {operatorId && !loading && error && (
        <div data-testid="rl-my-tickets-error" className="rounded-md border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800">
          {error.detail || error.message || `${t("dashboard.error_prefix")} ${error.status || ""}`}
        </div>
      )}

      {operatorId && !loading && !error && totalActive === 0 && (
        <div data-testid="rl-my-tickets-empty-for-operator" className="rounded-md border border-dashed border-slate-300 bg-white p-6 text-center text-xs text-slate-500">
          {t("my_tickets.empty_for_operator")}
        </div>
      )}

      {operatorId && !loading && !error && totalActive > 0 && (
        <div data-testid="rl-my-tickets-groups" className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          {visibleGroups.map((g) => {
            const rows = grouped[g] || [];
            if (rows.length === 0) return null;
            const cls = GROUP_STYLES[g] || "bg-slate-50 border-slate-200 text-slate-700";
            return (
              <div
                key={g}
                data-testid={`rl-my-tickets-group-${g}`}
                className={`rounded-md border ${cls} p-3 flex flex-col gap-2`}
              >
                <header className="flex items-baseline justify-between">
                  <h3 className="text-sm font-semibold">{t(labelKeyFor(TICKET_STATUS_LABEL_KEY, g))}</h3>
                  <span data-testid={`rl-my-tickets-group-${g}-count`} className="text-[10px] font-mono opacity-75">
                    {rows.length}
                  </span>
                </header>
                <ul className="flex flex-col gap-1.5">
                  {rows.map((row) => (
                    <li
                      key={row.id}
                      data-testid={`rl-my-tickets-row-${row.id}`}
                      onClick={() => navigate(`/admin/racket-lab/tickets/${row.id}`)}
                      className="cursor-pointer rounded border border-white/40 bg-white/70 p-2 text-xs hover:bg-white"
                    >
                      <div className="flex items-baseline justify-between gap-2">
                        <code className="text-[10px] font-mono text-slate-700">
                          {row.short_code || row.id?.slice(0, 8)}
                        </code>
                        <span className="text-[10px] text-slate-500">
                          {row.created_at?.slice(5, 16).replace("T", " ")}
                        </span>
                      </div>
                      <div className="mt-1 truncate text-slate-800">
                        {row.equipment_reference_label || "—"}
                      </div>
                      {row.customer_reference_label && (
                        <div className="truncate text-[10px] text-slate-500">
                          {row.customer_reference_label}
                        </div>
                      )}
                      {(() => {
                        const warn = stockWarningFor(row);
                        if (!warn) return null;
                        const insufficient = warn.type === "insufficient";
                        return (
                          <div
                            data-testid={`rl-my-tickets-stock-warning-${row.id}`}
                            className={`mt-1.5 inline-flex flex-wrap items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-semibold ${
                              insufficient
                                ? "border-rose-300 bg-rose-100 text-rose-800"
                                : "border-amber-300 bg-amber-100 text-amber-800"
                            }`}
                          >
                            <AlertTriangle className="h-3 w-3 shrink-0" />
                            <span>
                              {t(insufficient ? "my_tickets.insufficient_stock_badge" : "my_tickets.low_stock_badge")}
                            </span>
                            <span className="font-normal opacity-80">
                              · {t("my_tickets.stock_hint", {
                                available: warn.item.available_quantity,
                                unit: t(`inventory.units.${warn.item.unit}`),
                                needed: warn.item.consumption_per_ticket,
                              })}
                            </span>
                          </div>
                        );
                      })()}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RLAdminMyTickets;
